import React, { createContext, useContext, useState, useCallback } from 'react';
import { Node, Edge } from 'reactflow';
import * as graphService from '../../services/graphService';
import { GraphData, GraphFilter } from '../../types/graph';

interface GraphContextType {
  isLoading: boolean;
  error: Error | null;
  loadGraphData: (sessionId: string) => Promise<GraphData>;
  saveGraphLayout: (sessionId: string, nodes: Node[], edges: Edge[]) => Promise<void>;
  expandNode: (nodeId: string, nodeType: string) => Promise<GraphData>;
  collapseNode: (nodeId: string) => GraphData;
  loadLineageGraph: (rootUid: string) => Promise<GraphData>;
  filterGraph: (sessionId: string, filter: GraphFilter) => Promise<GraphData>;
  exportGraphAsImage: (sessionId: string, format: 'png' | 'svg' | 'jpg') => Promise<string>;
  importGraphLayout: (sessionId: string, layoutFile: File) => Promise<GraphData>;
}

const GraphContext = createContext<GraphContextType | undefined>(undefined);

export const GraphProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const loadGraphData = useCallback(async (sessionId: string) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const data = await graphService.getSessionGraph(sessionId);
      setIsLoading(false);
      return data;
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to load graph data'));
      setIsLoading(false);
      return { nodes: [], edges: [] };
    }
  }, []);

  const saveGraphLayout = useCallback(async (sessionId: string, nodes: Node[], edges: Edge[]) => {
    try {
      await graphService.saveGraphLayout(sessionId, nodes, edges);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to save graph layout'));
    }
  }, []);

  const expandNode = useCallback(async (nodeId: string, nodeType: string) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const data = await graphService.expandNode(nodeId, nodeType);
      setIsLoading(false);
      return data;
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to expand node'));
      setIsLoading(false);
      return { nodes: [], edges: [] };
    }
  }, []);

  const collapseNode = useCallback((nodeId: string) => {
    // This would typically involve filtering the nodes and edges
    // to remove the expanded children of the node
    return { nodes: [], edges: [] };
  }, []);
  
  const filterGraph = useCallback(async (sessionId: string, filter: GraphFilter) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const data = await graphService.filterGraph(sessionId, filter);
      setIsLoading(false);
      return data;
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to filter graph'));
      setIsLoading(false);
      return { nodes: [], edges: [] };
    }
  }, []);
  
  const exportGraphAsImage = useCallback(async (sessionId: string, format: 'png' | 'svg' | 'jpg' = 'png') => {
    try {
      return await graphService.exportGraphAsImage(sessionId, format);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to export graph'));
      throw err;
    }
  }, []);
  
  const importGraphLayout = useCallback(async (sessionId: string, layoutFile: File) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const data = await graphService.importGraphLayout(sessionId, layoutFile);
      setIsLoading(false);
      return data;
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to import graph layout'));
      setIsLoading(false);
      return { nodes: [], edges: [] };
    }
  }, []);
  
  const loadLineageGraph = useCallback(async (rootUid: string) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const data = await graphService.getLineageGraph(rootUid);
      setIsLoading(false);
      return data;
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to load lineage graph'));
      setIsLoading(false);
      return { nodes: [], edges: [] };
    }
  }, []);

  return (
    <GraphContext.Provider
      value={{
        isLoading,
        error,
        loadGraphData,
        saveGraphLayout,
        expandNode,
        collapseNode,
        loadLineageGraph,
        filterGraph,
        exportGraphAsImage,
        importGraphLayout
      }}
    >
      {children}
    </GraphContext.Provider>
  );
};

export const useGraphContext = () => {
  const context = useContext(GraphContext);
  if (context === undefined) {
    throw new Error('useGraphContext must be used within a GraphProvider');
  }
  return context;
};
